import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { showToast } from '../utils/toast';
import { API_BASE_URL } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Session timeout: 23 hours (token expires in 24h, refresh before)
const SESSION_TIMEOUT = 23 * 60 * 60 * 1000; // 23 hours in milliseconds
const WARNING_TIME = 5 * 60 * 1000; // Warn 5 minutes before timeout

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const timeoutWarningRef = useRef(null);
  const sessionTimeoutRef = useRef(null);

  // Set up axios interceptor to always include token and handle token refresh
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    // Request interceptor to add token to every request
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token refresh and 401 errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If 401 and not already retrying, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          // Clear session and redirect to login
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
          showToast.error('Session expired. Please login again.');
          
          // Redirect to login (will be handled by ProtectedRoute)
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Set up session timeout warning
  const setupSessionTimeout = () => {
    // Clear existing timeouts
    if (timeoutWarningRef.current) {
      clearTimeout(timeoutWarningRef.current);
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }

    // Set warning timeout (5 minutes before session expires)
    timeoutWarningRef.current = setTimeout(() => {
      const shouldExtend = window.confirm(
        'Your session will expire in 5 minutes. Would you like to extend it?'
      );
      
      if (shouldExtend) {
        // Extend session by refreshing token (if backend supports it)
        // For now, just reset the timeout
        setupSessionTimeout();
      }
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set session timeout
    sessionTimeoutRef.current = setTimeout(() => {
      showToast.error('Your session has expired. Please login again.');
      logout();
    }, SESSION_TIMEOUT);
  };

  // Check for existing session on mount
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Set up session timeout
      setupSessionTimeout();
      
      // Fetch updated user info to get meal_count and workout_count
      if (!parsedUser.meal_count || !parsedUser.workout_count) {
        axios.get(`${API_BASE_URL}/api/users/${parsedUser.id}`)
          .then(response => {
            const updatedUser = { 
              ...parsedUser, 
              meal_count: response.data.meal_count || 6,
              workout_count: response.data.workout_count || 6
            };
            setUser(updatedUser);
            localStorage.setItem('user', JSON.stringify(updatedUser));
          })
          .catch(err => console.error('Error fetching user:', err));
      }
    }

    return () => {
      if (timeoutWarningRef.current) {
        clearTimeout(timeoutWarningRef.current);
      }
      if (sessionTimeoutRef.current) {
        clearTimeout(sessionTimeoutRef.current);
      }
    };
  }, []);

  const login = async (username, password) => {
    try {
      // Skip account lockout for super_admin (username: 'admin')
      const isSuperAdmin = username.toLowerCase() === 'admin';
      
      // Check for account lockout (skip for super_admin)
      if (!isSuperAdmin) {
        const lockoutKey = `login_attempts_${username}`;
        const lockoutData = localStorage.getItem(lockoutKey);
        
        if (lockoutData) {
          const { attempts, lockoutUntil } = JSON.parse(lockoutData);
          const now = Date.now();
          
          if (lockoutUntil > now) {
            const minutesLeft = Math.ceil((lockoutUntil - now) / 60000);
            return { 
              success: false, 
              error: `Account locked. Please try again in ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}.` 
            };
          }
        }
      }

      const response = await axios.post(`${API_BASE_URL}/api/login`, {
        username,
        password
      });

      const { token, user: userData } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Clear login attempts on successful login (skip for super_admin)
      if (!isSuperAdmin) {
        const lockoutKey = `login_attempts_${username}`;
        localStorage.removeItem(lockoutKey);
      }
      
      setUser(userData);
      setIsAuthenticated(true);
      
      // Set up session timeout
      setupSessionTimeout();
      
      return { success: true };
    } catch (error) {
      // Skip account lockout tracking for super_admin
      const isSuperAdmin = username.toLowerCase() === 'admin';
      
      if (isSuperAdmin) {
        // Super admin - no lockout, just return the error
        return { 
          success: false, 
          error: error.response?.data?.error || 'Invalid credentials' 
        };
      }
      
      // Track failed login attempts (for non-super-admin users)
      const lockoutKey = `login_attempts_${username}`;
      const lockoutData = localStorage.getItem(lockoutKey);
      const attempts = lockoutData ? JSON.parse(lockoutData).attempts : 0;
      const newAttempts = attempts + 1;
      
      // Lock account after 5 failed attempts for 15 minutes
      if (newAttempts >= 5) {
        const lockoutUntil = Date.now() + (15 * 60 * 1000); // 15 minutes
        localStorage.setItem(lockoutKey, JSON.stringify({
          attempts: newAttempts,
          lockoutUntil
        }));
        return { 
          success: false, 
          error: 'Too many failed login attempts. Account locked for 15 minutes.' 
        };
      } else {
        localStorage.setItem(lockoutKey, JSON.stringify({
          attempts: newAttempts,
          lockoutUntil: 0
        }));
        return { 
          success: false, 
          error: error.response?.data?.error || `Login failed. ${5 - newAttempts} attempt${5 - newAttempts > 1 ? 's' : ''} remaining.` 
        };
      }
    }
  };

  const logout = () => {
    // Clear session timeouts
    if (timeoutWarningRef.current) {
      clearTimeout(timeoutWarningRef.current);
    }
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

